import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { formatDate, getAvatarColor, getFirstTwoLetters } from '../../common';
import { SYSTEM_USER_ID } from '../../config';
import { processMessageText } from '../../utils/messageUtils';
import { isVoiceMessage } from '../../utils/incomingMessage';
import { useNotification } from '../../NotificationContext';
import { useT } from '../../LocaleContext';
import TypingMessage from './TypingMessage';
import VoicePlayer from './VoicePlayer';
import {
  MessageWrapper,
  Message,
  MessageBubble,
  StyledMessageContent,
  MessageTimestamp,
  Avatar,
  AvatarImage,
  AvatarSpacer,
  MessageMenuDropdown,
  MessageMenuItem,
  GroupSenderLabel,
} from '../../styles/UserWorkspacePageStyles';

const MessageItem = ({
  msg,
  isUser,
  user,
  chat,
  userAvatarUrl,
  peerAvatars,
  chatTrustPolicies,
  selectedChatId,
  onDeleteMessage,
  showAvatar = false,
  showSenderName = false,
  isGrouped = false,
  isFirstAfterDate = false,
  groupSenderNames,
}) => {
  const { showNotification } = useNotification();
  const t = useT();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const messageRef = useRef(null);
  const menuRef = useRef(null);
  
  const handleFileDownloadError = (message) => {
    showNotification(message, 'error', 10000);
  };

  // Get file from attachments
  const fileAttachment = (msg.attachments && Array.isArray(msg.attachments) && msg.attachments.length > 0) 
    ? msg.attachments[0] 
    : null;

  const hasFile = fileAttachment && msg.message_type === 'file_message';
  const hasVoice = isVoiceMessage(msg);
  const hasText = msg.message && msg.message_type !== 'file_message' && msg.message_type !== 'voice_message' && msg.message.trim().length > 0;
  const isTyping = msg.online === 'true' && (msg.sender === 'system' || msg.sender_id === SYSTEM_USER_ID);
  const hasRenderableContent = hasFile || hasVoice || hasText || isTyping;
  const canOpenMenu = (isUser && msg.id) || hasFile || hasText || hasVoice;

  const renderMessageBody = () => {
    if (hasVoice) {
      return (
        <VoicePlayer
          attachment={{
            ...fileAttachment,
            e2e_header: fileAttachment?.e2e_header || msg.e2e_header,
            e2e: fileAttachment?.e2e || Boolean(msg.e2e_version),
            e2e_version: fileAttachment?.e2e_version || msg.e2e_version,
          }}
          chatId={msg.chat_id || selectedChatId}
          msgId={msg.id}
          durationMs={msg.meta?.duration_ms || fileAttachment?.duration_ms || 0}
          peaks={msg.meta?.peaks || fileAttachment?.peaks}
        />
      );
    }
    const content = processMessageText(msg.message, isUser, msg.meta, msg.message_type, handleFileDownloadError, msg.attachments);
    if (msg.message_type === 'file_message' && !msg.attachments && !content) {
      return null;
    }
    return <div>{content}</div>;
  };

  const renderTimestamp = () => (
    <MessageTimestamp>
      {formatDate(msg.created_at)}
    </MessageTimestamp>
  );

  // Функция для расчета позиции меню с учетом границ viewport
  const calculateMenuPosition = () => {
    if (!messageRef.current) return null;
    
    const rect = messageRef.current.getBoundingClientRect();
    const menuSpacing = 2; // Уменьшено расстояние между сообщением и меню снизу
    const menuSpacingAbove = 2; // Отступ сверху (когда меню показывается над сообщением)
    const estimatedMenuHeight = 150; // Примерная высота меню (будет уточнена после рендера)
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Проверяем, помещается ли меню снизу
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const showAbove = spaceBelow < estimatedMenuHeight + menuSpacing && spaceAbove > spaceBelow;
    
    // Вычисляем позицию по вертикали
    let top;
    if (showAbove) {
      // Показываем меню сверху сообщения - нижний край меню должен быть близко к верхнему краю сообщения
      // top + menuHeight должно быть близко к rect.top
      // top = rect.top - menuHeight - menuSpacingAbove
      top = rect.top - estimatedMenuHeight - menuSpacingAbove;
      // Если и сверху не помещается, показываем максимально близко к верхнему краю
      if (top < menuSpacingAbove) {
        top = menuSpacingAbove;
      }
    } else {
      // Показываем меню снизу сообщения - ближе к сообщению
      top = rect.bottom + menuSpacing;
      // Если не помещается снизу, показываем максимально близко к нижнему краю, но стараемся сохранить близость к сообщению
      if (top + estimatedMenuHeight > viewportHeight - menuSpacing) {
        // Пробуем показать сверху, если там больше места
        const topPositionAbove = rect.top - estimatedMenuHeight - menuSpacingAbove;
        if (topPositionAbove >= menuSpacingAbove) {
          top = topPositionAbove;
        } else {
          // Иначе максимально близко к нижнему краю
          top = Math.max(menuSpacing, viewportHeight - estimatedMenuHeight - menuSpacing);
        }
      }
    }
    
    // Вычисляем позицию по горизонтали (right)
    const menuWidth = 200; // Примерная ширина меню
    let right = viewportWidth - rect.right;
    
    // Проверяем, не выходит ли меню за правый край
    if (right < menuSpacing) {
      right = menuSpacing;
    }
    if (rect.right - menuWidth < menuSpacing) {
      right = viewportWidth - rect.left - menuWidth;
      if (right < menuSpacing) {
        right = menuSpacing;
      }
    }
    
    return { top, right };
  };

  const handleMessageClick = (e) => {
    if (!canOpenMenu) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    if (isMenuOpen) {
      setIsMenuOpen(false);
      return;
    }
    
    const position = calculateMenuPosition();
    if (position) {
      setMenuPosition(position);
      setIsMenuOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen || !messageRef.current) return;

    const updatePosition = () => {
      const position = calculateMenuPosition();
      if (position) {
        setMenuPosition(position);
      }
    };

    // Обновляем позицию после небольшой задержки, чтобы меню успело отрендериться
    const timeoutId = setTimeout(updatePosition, 0);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isMenuOpen]);

  // Дополнительный эффект для уточнения позиции после рендера меню
  useEffect(() => {
    if (!isMenuOpen || !menuRef.current || !messageRef.current) return;

    const adjustPosition = () => {
      const menuRect = menuRef.current.getBoundingClientRect();
      const messageRect = messageRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const menuSpacing = 2; // Отступ снизу (когда меню показывается под сообщением)
      const menuSpacingAbove = 2; // Отступ сверху (когда меню показывается над сообщением)

      setMenuPosition((currentPosition) => {
        if (!currentPosition) return currentPosition;

        let top = currentPosition.top;
        let right = currentPosition.right;

        // Определяем, должно ли меню быть сверху или снизу сообщения
        const isMenuAbove = currentPosition.top < messageRect.top;
        const idealTopAbove = messageRect.top - menuRect.height - menuSpacingAbove;
        const idealTopBelow = messageRect.bottom + menuSpacing;

        // Проверяем нижнюю границу - если меню снизу и выходит за границу
        if (!isMenuAbove && menuRect.bottom > viewportHeight - menuSpacing) {
          // Пробуем показать сверху сообщения, если там больше места
          if (idealTopAbove >= menuSpacingAbove) {
            top = idealTopAbove;
          } else {
            // Иначе максимально близко к нижнему краю, но стараемся сохранить близость
            top = Math.max(idealTopBelow, viewportHeight - menuRect.height - menuSpacing);
          }
        }

        // Проверяем верхнюю границу - если меню сверху и выходит за границу
        if (isMenuAbove && menuRect.top < menuSpacingAbove) {
          // Стараемся сохранить близость к сообщению - нижний край меню должен быть близко к верхнему краю сообщения
          // Но если не помещается, делаем минимальный отступ от верха
          if (idealTopAbove >= menuSpacingAbove) {
            top = idealTopAbove;
          } else {
            // Если не помещается даже с минимальным отступом, показываем максимально близко к верху
            top = menuSpacingAbove;
          }
        }

        // Если меню сверху, убеждаемся что оно близко к сообщению (не слишком далеко)
        if (isMenuAbove) {
          const bottomOfMenu = top + menuRect.height;
          const distanceFromMessage = messageRect.top - bottomOfMenu;
          // Если меню слишком далеко от сообщения (больше чем menuSpacingAbove + небольшой запас), приближаем его
          if (distanceFromMessage > menuSpacingAbove + 5) {
            top = messageRect.top - menuRect.height - menuSpacingAbove;
            // Но не выходим за верхнюю границу
            if (top < menuSpacingAbove) {
              top = menuSpacingAbove;
            }
          }
        }

        // Проверяем правую границу
        if (menuRect.right > viewportWidth - menuSpacing) {
          right = viewportWidth - menuRect.width - menuSpacing;
          if (right < menuSpacing) {
            right = menuSpacing;
          }
        }

        // Проверяем левую границу
        if (menuRect.left < menuSpacing) {
          right = viewportWidth - messageRect.left - menuRect.width;
          if (right < menuSpacing) {
            right = menuSpacing;
          }
        }

        // Обновляем только если позиция изменилась
        if (Math.abs(top - currentPosition.top) > 1 || Math.abs(right - currentPosition.right) > 1) {
          return { top, right };
        }

        return currentPosition;
      });
    };

    const timeoutId = setTimeout(adjustPosition, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isMenuOpen]);

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    if (onDeleteMessage && msg.id) {
      onDeleteMessage(msg.id);
    }
  };

  const handleCopyText = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    
    const textToCopy = msg.message;
    if (!textToCopy) return;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      showNotification(t('chat.textCopied'), 'success', 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback для старых браузеров
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showNotification(t('chat.textCopied'), 'success', 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
        handleFileDownloadError('Failed to copy text');
      }
    }
  };

  let senderName;
  if (isUser) {
    senderName = user.user_name || (user.email ? user.email.substring(0, 2).toUpperCase() : 'you');
  } else if (chat?.chat_type === 'group') {
    const senderIdKey = msg.sender_id ? String(msg.sender_id).toLowerCase() : '';
    senderName = msg.sender_name
      || (senderIdKey && groupSenderNames?.[senderIdKey])
      || (msg.sender_connect_id && groupSenderNames?.[msg.sender_connect_id])
      || msg.sender_connect_id
      || 'unknown';
  } else {
    senderName = chat?.peer_name || 'Unknown';
  }
  
  const isGroupChat = chat?.chat_type === 'group';
  const initials = isUser 
    ? (user.user_name ? getFirstTwoLetters(user.user_name) : (user.email ? user.email.substring(0, 2).toUpperCase() : 'you'))
    : getFirstTwoLetters(senderName);
  
  let peerConnectId = null;
  if (!isUser) {
    peerConnectId = chat?.peer_connect_id || msg.sender_connect_id;
  }
  
  let avatarUrl = null;
  if (isUser) {
    avatarUrl = userAvatarUrl;
  } else {
    // Проверяем, является ли это системным сообщением
    const isSystemMsg = msg.sender === 'system' || 
                       msg.sender_id === SYSTEM_USER_ID ||
                       !msg.sender_connect_id;
    if (isSystemMsg) {
      avatarUrl = '/robot.png';
    } else if (chat?.peer_name === 'favorites') {
      // Для favorites чата используем аватар пользователя
      avatarUrl = userAvatarUrl;
    } else if (chat?.chat_type === 'group' && peerConnectId) {
      avatarUrl = peerAvatars[peerConnectId] || null;
    } else if (peerConnectId) {
      // Check trust policy before showing avatar - privacy first
      const trustPolicy = chatTrustPolicies?.[chat?.id];
      
      if (trustPolicy && trustPolicy.shareAvatar) {
        // Peer shares avatar - show it
        avatarUrl = peerAvatars[peerConnectId] || null;
      } else {
        // Trust policy not loaded yet OR peer doesn't share avatar - use default (null)
        avatarUrl = null;
      }
    }
  }

  const avatarColor = isUser
    ? getAvatarColor(user?.connect_id || '')
    : getAvatarColor(peerConnectId || senderName || '');

  // Создаем уникальный ключ: используем ID сообщения, если есть, иначе комбинацию индекса и временной метки
  // Добавляем chat_id для дополнительной уникальности
  const messageKey = msg.id 
    ? `msg-${msg.id}-${msg.chat_id || selectedChatId || 'unknown'}` 
    : `msg-${Date.now()}-${msg.chat_id || selectedChatId || 'unknown'}`;

  if (!hasRenderableContent) {
    return null;
  }
  
  return (
    <>
      {isMenuOpen && menuPosition && createPortal(
        <div
          ref={menuRef}
          data-portal-message-menu
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            zIndex: 10000,
            pointerEvents: 'auto',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageMenuDropdown isOpen={true}>
            {hasText && (
              <MessageMenuItem 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCopyText(e);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {t('chat.copyText')}
              </MessageMenuItem>
            )}
            {isUser && (
              <MessageMenuItem 
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(e);
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                $danger
              >
                {t('chat.deleteForEveryone')}
              </MessageMenuItem>
            )}
          </MessageMenuDropdown>
        </div>,
        document.body
      )}
      <MessageWrapper 
        key={messageKey} 
        isUser={isUser}
        isGrouped={isGrouped}
        $isFirstAfterDate={isFirstAfterDate}
        $isGroupChat={isGroupChat}
        ref={messageRef}
      >
      {!isUser ? (
        <>
          {isGroupChat && (
            showAvatar ? (
              avatarUrl ? (
                <Avatar color={avatarColor}>
                  <AvatarImage 
                    src={avatarUrl} 
                    alt={senderName}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const avatarContainer = e.target.parentElement;
                      if (avatarContainer && !avatarContainer.querySelector('.avatar-fallback')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'avatar-fallback';
                        fallback.textContent = initials;
                        fallback.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 600;';
                        avatarContainer.appendChild(fallback);
                      }
                    }}
                  />
                </Avatar>
              ) : (
                <Avatar color={avatarColor}>
                  {initials}
                </Avatar>
              )
            ) : (
              <AvatarSpacer />
            )
          )}
          <Message isUser={isUser}>
            {showSenderName && (
              <GroupSenderLabel title={senderName}>
                {senderName}
              </GroupSenderLabel>
            )}
            {msg.online === "false" ? (
              <MessageBubble isUser={isUser} $voice={hasVoice} onClick={canOpenMenu ? handleMessageClick : undefined} style={{ cursor: canOpenMenu ? 'pointer' : 'default' }}>
                <StyledMessageContent>
                  {renderMessageBody()}
                </StyledMessageContent>
                {renderTimestamp()}
              </MessageBubble>
            ) : (
              <>
                {/* Если это текущее сообщение от системы, показываем эффект печати */}
                {msg.online === "true" && (msg.sender === "system" || msg.sender_id === SYSTEM_USER_ID) ? (
                  <TypingMessage text={msg.message} isUser={msg.sender === user.user_id} createdAt={msg.created_at}>
                    <StyledMessageContent>
                      {renderMessageBody()}
                    </StyledMessageContent>
                  </TypingMessage>
                ) : (
                  <MessageBubble isUser={isUser} $voice={hasVoice} onClick={canOpenMenu ? handleMessageClick : undefined} style={{ cursor: canOpenMenu ? 'pointer' : 'default' }}>
                    <StyledMessageContent>
                      {renderMessageBody()}
                    </StyledMessageContent>
                    {renderTimestamp()}
                  </MessageBubble>
                )}
              </>
            )}
          </Message>
        </>
      ) : (
        <>
          <Message isUser={isUser}>
            {/* Проверяем, если сообщение старое, не используем TypingMessage */}
            {msg.online === "false" ? (
              <MessageBubble isUser={isUser} $voice={hasVoice} onClick={canOpenMenu ? handleMessageClick : undefined} style={{ cursor: canOpenMenu ? 'pointer' : 'default' }}>
                <StyledMessageContent>
                  {renderMessageBody()}
                </StyledMessageContent>
                {renderTimestamp()}
              </MessageBubble>
            ) : (
              <>
                {/* Если это текущее сообщение от системы, показываем эффект печати */}
                {msg.online === "true" && (msg.sender === "system" || msg.sender_id === SYSTEM_USER_ID) ? (
                  <TypingMessage text={msg.message} isUser={msg.sender === user.user_id} createdAt={msg.created_at}>
                    <StyledMessageContent>
                      {renderMessageBody()}
                    </StyledMessageContent>
                  </TypingMessage>
                ) : (
                  <MessageBubble isUser={isUser} $voice={hasVoice} onClick={canOpenMenu ? handleMessageClick : undefined} style={{ cursor: canOpenMenu ? 'pointer' : 'default' }}>
                    <StyledMessageContent>
                      {renderMessageBody()}
                    </StyledMessageContent>
                    {renderTimestamp()}
                  </MessageBubble>
                )}
              </>
            )}
          </Message>
          {isGroupChat && (
            showAvatar ? (
              userAvatarUrl ? (
                <Avatar color={getAvatarColor(user?.connect_id || '')}>
                  <AvatarImage 
                    src={userAvatarUrl} 
                    alt={user?.user_name || 'You'}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const avatarContainer = e.target.parentElement;
                      if (avatarContainer && !avatarContainer.querySelector('.avatar-fallback')) {
                        const fallback = document.createElement('div');
                        fallback.className = 'avatar-fallback';
                        fallback.textContent = getFirstTwoLetters(user?.user_name || user?.email || 'U');
                        fallback.style.cssText = 'width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 600;';
                        avatarContainer.appendChild(fallback);
                      }
                    }}
                  />
                </Avatar>
              ) : (
                <Avatar color={getAvatarColor(user?.connect_id || '')}>
                  {getFirstTwoLetters(user?.user_name || user?.email || 'U')}
                </Avatar>
              )
            ) : (
              <AvatarSpacer />
            )
          )}
        </>
      )}
    </MessageWrapper>
    </>
  );
};

export default MessageItem;

